import React, { useEffect } from 'react';
import { FormikProps } from 'formik';
import { IFileFrom } from 'types/FileType';
import { uploadToS3 } from 'utils/uploadToS3';
import { useTranslation } from 'react-i18next';
import { FormDetailModel } from 'types/FormTypes';
import { useGlobal } from 'context/global/GlobalContext';
import { showToast } from 'components/atomic/Toast/Toast';
import { useFormContext } from 'context/FormDrawerContext/FormDrawerContext';
import LoadingIndicator from 'components/atomic/LoadingIndicator';
import FileUpload from '../../../atomic/FileUpload';
import FormImage from './FormImage';

type FormFileUploadProps = {
  formData: FormDetailModel;
  formRef: FormikProps<any> | null;
  name: string;
  updateFormField: (name: string, value: any[]) => Promise<void>;
};

const FormFileUpload: React.FC<FormFileUploadProps> = ({
  formData,
  formRef,
  name,
  updateFormField,
}) => {
  const { setBackDisabled, setNextDisabled, setFileUploading, fileUploading } =
    useFormContext();

  const { productDetails } = useGlobal();
  const { t } = useTranslation('translation', {
    keyPrefix: 'drawers.formDrawer.fileUpload',
  });

  useEffect(() => {
    if (formData.isRequired) {
      const existingValue = formRef?.values[name] || [];
      const isValid = Array.isArray(existingValue) && existingValue.length > 0;

      if (isValid) {
        setNextDisabled(false);
        formRef?.setFieldError(name, undefined);
      } else {
        setNextDisabled(true);
        formRef?.setFieldError(name, t('requiredError'));
      }
    } else {
      setNextDisabled(false);
      formRef?.setFieldError(name, undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, formRef?.values[name]]);

  const changeSelectedFile = async (files: FileList) => {
    setBackDisabled(true);
    setNextDisabled(true);
    setFileUploading(true);
    if (!files?.length) {
      updateFormField(name, []);
      setFileUploading(false);
      return;
    }

    const uploaded_files = [...files];
    let updatedSelectedFiles: IFileFrom[] = formRef?.values[name] || [];
    const uploadFilePromiseArr = [];

    for (let i = 0; i < uploaded_files?.length; i++) {
      // only allow images and pdf
      if (
        ![
          'application/pdf',
          'image/png',
          'image/jpg',
          'image/jpeg',
          'video/mp4',
          'video/mkv',
        ].includes(uploaded_files[i].type)
      ) {
        showToast({
          message: t('fileExtensionError'),
          type: 'error',
        });
        setFileUploading(false);
        return;
      }

      let fileObject: IFileFrom = {
        fileName: uploaded_files[i]?.name,
        fileUrl: '',
      };

      uploadFilePromiseArr.push(async () => {
        try {
          const url = await uploadToS3(uploaded_files[i]);
          if (!url) {
            showToast({
              message: `File "${
                uploaded_files[i].name.length > 30
                  ? `${uploaded_files[i].name.substring(0, 18)}...`
                  : uploaded_files[i].name
              }"${t('failedToUpload')}`,
              type: 'error',
            });
            return {
              ...fileObject,
              fileUrl: '',
            };
          }

          return {
            ...fileObject,
            fileUrl: url,
            needsProcessing:
              !!productDetails?.automaticReceiptApprovalEnabled &&
              formData?.property?.name === 'receiptUpload',
            processingData: {
              brandId: productDetails?.brand.id,
              approvalMode: 'Lenient',
              slug: (
                window.location.pathname.match(/\/c\/[a-z0-9]+/i) as any
              )[0]
                .split('/')
                .pop(),
            },
          };
        } catch (e) {
          throw e;
        }
      });
    }

    if (uploadFilePromiseArr.length) {
      try {
        let results = await Promise.all(uploadFilePromiseArr.map((p) => p()));
        updatedSelectedFiles = updatedSelectedFiles.concat(
          results.filter((result) => result.fileUrl !== '')
        );
      } catch (e) {
        showToast({
          message: t('receiptUploadError'),
          type: 'error',
        });
        return;
      }
    }

    await updateFormField(name, updatedSelectedFiles);
    await formRef?.validateForm();
    setBackDisabled(false);
    setNextDisabled(false);
    setFileUploading(false);
  };

  return formRef ? (
    <div className='w-full h-full flex flex-col justify-start px-3 overflow-auto'>
      {formData.image && (
        <FormImage image={formData.image} alt='form-question-image' />
      )}
      <h5
        tabIndex={0}
        className={`text-left text-lg leading-formTitle font-semibold text-black ${
          !formData.subText ? 'mb-3' : 'mb-0'
        }`}
      >
        {formData.text}
      </h5>
      {formData.subText && (
        <p
          tabIndex={0}
          className='mt-1.5 mb-3 text-left text-xs font-semibold leading-formSubtitle text-muted'
        >
          {formData.subText}
        </p>
      )}
      <div className='w-full flex flex-col items-center justify-start'>
        <FileUpload
          multiple
          uploadLimit={5}
          loading={fileUploading}
          changeFile={changeSelectedFile}
          selectedFiles={formRef.values[name]}
          uploadButtonText={formData?.config?.cta}
          setSelectedFiles={(files: IFileFrom[]) =>
            updateFormField(name, files)
          }
        />
      </div>
    </div>
  ) : (
    <LoadingIndicator />
  );
};

export default FormFileUpload;
